// Reminder-day chips (profile spec §3.1, §3.3, §7.2).
//
// This is the one field on a generated birthday row that stays editable, and
// it is editable by **both** members: a birthday is the member's own data
// (profile spec §2.1 P1), but whether you want to be reminded a week early is
// a shared setting. The birthday date itself is locked server-side with 422
// (`validate_auto_generated_patch`), and the TA tab says so in words.
//
// **The `+` affordance is a mockup gesture, not a design.** The mockup shows a
// bare `+` chip; spec §7.2 leaves the behaviour to the implementation. It
// opens a sheet listing the remaining legal values rather than offering a
// number field, because the server's allowlist is fixed
// (`ALLOWED_REMINDER_DAYS = {0,1,3,7,14,30}`, max 6) and a free-text input
// would let a client compose a value the backend rejects. The mockup's chip
// height is also 36px; DESIGN §15 requires 44px, so the real chips are 44.

import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api/client";
import { ApiError } from "@/lib/api/pair";
import { cn } from "@/lib/utils/cn";

/** Mirrors the backend allowlist in `anniversary/schemas.py`. */
const ALLOWED = [0, 1, 3, 7, 14, 30] as const;
const MAX_COUNT = 6;

const LABEL: Record<number, string> = {
  0: "當天",
  1: "提前 1 天",
  3: "提前 3 天",
  7: "提前 7 天",
  14: "提前 14 天",
  30: "提前 30 天",
};

export interface ReminderDaysFieldProps {
  profile: { birthday_anniversary_id: string | null; birthday_reminder_days: number[]; relation: string };
  canEdit: boolean;
  onChanged: () => void | Promise<void>;
}

export function ReminderDaysField({ profile, canEdit, onChanged }: ReminderDaysFieldProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  // Not re-sorted. The backend stores the largest gap first (`[7, 1, 0]`) and
  // that order is chronological for a "days before" set: a reminder list read
  // furthest-ahead-first is what a person scanning it expects. Sorting ascending
  // here turned it into `[0, 1, 7]`, which reads as "remind me the day of,
  // then a day later, then a week later" and loses the sense of lead time.
  const selected = [...profile.birthday_reminder_days];
  const available = ALLOWED.filter((d) => !selected.includes(d));

  async function patch(days: number[]) {
    if (!profile.birthday_anniversary_id) return;
    setBusy(true);
    setError(null);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = await (api as any).PATCH(`/api/v1/anniversaries/${profile.birthday_anniversary_id}`, {
        body: { reminder_days: days },
      });
      if (!res.response.ok) {
        let code = "UNKNOWN";
        try {
          const body = await res.response.clone().json();
          code = body?.error?.code ?? code;
        } catch {
          /* body was not the error envelope */
        }
        throw new ApiError(res.response.status, code, "提醒天數更新失敗");
      }
      setShowAdd(false);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "提醒天數更新失敗");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="flex flex-col gap-s-2"
      data-testid="profile-reminder-days"
      aria-label="生日提醒"
    >
      <h2 className="text-h2 text-ink-1">生日提醒</h2>
      <p className="text-meta text-ink-3">提醒以站內通知送出</p>

      {!profile.birthday_anniversary_id ? (
        <p className="text-body-sm text-ink-3" data-testid="profile-reminder-empty">
          填上生日後,這裡可以設定提前幾天提醒。
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-s-2">
            {selected.map((d) => (
              <button
                key={d}
                type="button"
                disabled={!canEdit || busy}
                onClick={() => void patch(selected.filter((x) => x !== d))}
                data-testid={`reminder-day-${d}`}
                data-selected="true"
                aria-label={`${LABEL[d]},點一下移除`}
                className={cn(
                  "tap-target press min-h-11 min-w-11 rounded-pill px-s-3 text-body-sm font-semibold tabular-nums",
                  "bg-ink-1 text-paper",
                  canEdit ? "hover:opacity-90" : "opacity-70",
                )}
              >
                {d}
              </button>
            ))}

            {canEdit && available.length > 0 && selected.length < MAX_COUNT ? (
              <button
                type="button"
                onClick={() => setShowAdd((v) => !v)}
                data-testid="reminder-add"
                aria-expanded={showAdd}
                aria-label="新增提醒天數"
                className="tap-target press h-11 w-11 flex items-center justify-center rounded-pill border border-hairline text-ink-2 hover:bg-black/5"
              >
                <Plus size={18} strokeWidth={1.5} aria-hidden="true" />
              </button>
            ) : null}
          </div>

          {canEdit && selected.length >= MAX_COUNT ? (
            <p className="text-meta text-ink-3">最多 {MAX_COUNT} 個提醒天數</p>
          ) : null}

          {showAdd ? (
            <div className="flex flex-wrap gap-s-2" data-testid="reminder-add-list">
              {available.map((d) => (
                <Button
                  key={d}
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  data-testid={`reminder-add-${d}`}
                  onClick={() => void patch([...selected, d].sort((a, b) => b - a))}
                >
                  {LABEL[d]}
                </Button>
              ))}
            </div>
          ) : null}

          {error ? (
            <p className="text-meta text-partner-strong" role="alert" data-testid="reminder-error">
              {error}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
